import { landmineNames } from '@mm/constants/landmine-names';
import MessengerAdCRUDActions from '@mm/state/actions/MessengerAdCRUDActions';
import { MessengerAdModel } from '@mm/state/model/MessengerAdModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import Moment from 'moment';
import React from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { GridCell } from '../../../../../common/components/Grid/Grid';
import TableGrid from '../../../../../common/components/TableGrid/TableGrid';
import { ThreeDotMenu } from '../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import * as routes from '../../../../../common/constants/routes';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import './MessengerTableWithAds.pcss';

type OuterProps = {
  messengerAds: MessengerAdModel[];
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = OuterProps & InnerProps;

const actions = [
  { name: 'Edit', value: 'edit' },
  { name: 'Delete', value: 'delete' },
];

const getStatusText = (messengerAd: MessengerAdModel, armLandmine: boolean) => {
  if (armLandmine) {
    return 'Pending Startup Upgrade';
  }

  if (messengerAd.active) {
    return 'Active';
  }

  return 'Draft';
};

const generateTableData = (
  props: Props,
  pro: boolean,
  armLandmine: boolean,
) => {
  const {
    showUpgradeModal,
    openAdsLimitModal,
    history,
    chatbotId,
    openDeleteModal,
    onDeleteEntity,
  } = props;

  const getOnEditEntityClick = (id: number) => {
    if (armLandmine) {
      if (pro) {
        openAdsLimitModal();
      } else {
        showUpgradeModal(landmineNames['MESSAGING-ADS__ADS-TABLE__EDIT-AD']);
      }
    } else {
      history.push(
        `${routes.CHATBOT_EDITOR}/${chatbotId}/messenger-ads/edit/${id}`,
      );
    }
  };

  const onSelectItem = (value: string, item: number, chatbotId: number) => {
    if (value === 'edit') {
      getOnEditEntityClick(item);
    }
    if (value === 'delete') {
      openDeleteModal(() => {
        onDeleteEntity(chatbotId, item);
      });
    }
  };

  return [
    {
      label: 'Ad Name',
      keyName: 'name',
      renderCell: (item: MessengerAdModel) => {
        return (
          <GridCell
            className="messenger-table-with-ads__table-name"
            onClick={() => getOnEditEntityClick(item.id)}
          >
            <strong>{item.name}</strong>
          </GridCell>
        );
      },
    },
    {
      label: 'Status',
      keyName: 'status',
      renderCell: (item: MessengerAdModel) => {
        return <GridCell>{getStatusText(item, armLandmine)}</GridCell>;
      },
    },
    {
      label: 'Updated',
      keyName: 'updatedAt',
      renderCell: (item: MessengerAdModel) => {
        const date = Moment(item.updatedAt).format('MM/DD/YYYY');
        return <GridCell>{date}</GridCell>;
      },
    },
    {
      label: 'Action',
      className: 'action-cell',
      renderCell: (item: MessengerAdModel) => (
        <GridCell overflowable className="action-cell">
          <ThreeDotMenu
            elements={actions}
            selectItem={(action: string) =>
              onSelectItem(action, item.id, chatbotId)
            }
          />
        </GridCell>
      ),
    },
  ];
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  onDeleteEntity: (botId: number, entityId: number) =>
    dispatch(MessengerAdCRUDActions.delete(botId, { id: entityId })),
  openAdsLimitModal: async () =>
    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: 'CTM_ADS_PRO_SYNC',
        feature: 'adsUnlimitedNewCtmAds',
        stayOnPage: true,
      }),
    ),
  showUpgradeModal: (landmineName: string) =>
    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName,
        feature: 'adsUnlimitedNewCtmAds',
        stayOnPage: true,
      }),
    ),
  openDeleteModal: (deleteCallback: () => any) =>
    dispatch(
      modalActions.openDeleteConfirmModal({
        headerText: 'Delete Ad?',
        deleteCallback,
      }),
    ),
});

const mapStateToProps = (state: StoreState) => ({
  chatbotId: activeChatbotIdSelector(state),
});

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
);

const MessengerTableWithAds = (props: Props) => {
  const { messengerAds } = props;

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const pro = !!featureFlags.adsOneNewCtmAd;
  const armLandmine =
    !pro ||
    (!featureFlags.adsUnlimitedNewCtmAds && (messengerAds?.length || 0) > 1);

  const data = generateTableData(props, pro, armLandmine);

  return (
    <TableGrid
      data={data}
      items={messengerAds}
      className="messenger-table-with-ads"
    />
  );
};

export default enhance(MessengerTableWithAds);
